// CB:BEGIN pm-project-detail-page
/**
 * PM Project Detail Page — /pm/projects/:id
 *
 * Full operational view of a single PM-origin project:
 *   - Project overview + property context
 *   - PM acknowledgment status / Sign & Activate
 *   - Bid list + contractor comparison
 *   - Contractor selection (awards bid + creates escrow)
 *   - Documents
 *   - Activity log
 *   - PM notes vs contractor-visible notes
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@clerk/clerk-react';
import { getApiUrl } from '../lib/getApiUrl';
import SharedHeaderPropertyManager from '../components/SharedHeaderPropertyManager';
import ContentLimiter from '../components/layout/ContentLimiter';
import { ArrowLeft, Building2, ClipboardList, FileText, CheckCircle2, AlertTriangle, DollarSign, Clock, Users } from 'lucide-react';
import PMProjectAgreementModal from '../components/PMProjectAgreementModal'; // CB:BEGIN pm-project-agreement-modal-import

const BRAND_GREEN = '#123c3b';

interface Property { id: string; label?: string; address1: string; city: string; state: string; zip: string; propertyType: string; }
interface Bid {
  id: string;
  amount?: number | null;
  isAccepted?: boolean;
  note?: string | null;
  createdAt: string;
  status?: string;
  contractor?: { id: string; company: string; user?: { email: string } };
}
interface Project {
  id: string;
  address: string;
  city?: string;
  state?: string;
  zip?: string;
  projectType: string;
  status: string;
  priority: string;
  requestorType: string;
  requestedResponseBy?: string;
  requestedSiteVisitBy?: string;
  extraInfo?: string;
  pmNotes?: string;
  createdAt: string;
  updatedAt?: string;
  statusEnteredAt?: string;
  property?: Property;
  bids?: Bid[];
  metadata?: Record<string, any>;
}

const STATUS_COLORS: Record<string, { bg: string; color: string; label: string }> = {
  DRAFT: { bg: '#fef3c7', color: '#92400e', label: 'Draft — Signature Required' },
  POSTED: { bg: '#dbeafe', color: '#1d4ed8', label: 'Posted — Awaiting Bids' },
  BIDS_COLLECTING: { bg: '#fef9c3', color: '#854d0e', label: 'Collecting Bids' },
  BIDS_READY: { bg: '#dcfce7', color: '#166534', label: 'Bids Ready — Review Now' },
  DECIDING: { bg: '#fef9c3', color: '#854d0e', label: 'Deciding' },
  BID_SELECTED: { bg: '#e0f2fe', color: '#0369a1', label: 'Contractor Selected' },
  HOMEOWNER_CONTRACT_SIGNING: { bg: '#f3e8ff', color: '#7e22ce', label: 'Documents Preparation' },
  ESCROW_PENDING: { bg: '#fef3c7', color: '#92400e', label: 'Escrow Setup' },
  IN_PROGRESS: { bg: '#e0f2fe', color: '#0369a1', label: 'In Progress' },
  ACTIVE_P1: { bg: '#e0f2fe', color: '#0369a1', label: 'Active — Phase 1' },
  ACTIVE_P2: { bg: '#e0f2fe', color: '#0369a1', label: 'Active — Phase 2' },
  ACTIVE_P3: { bg: '#e0f2fe', color: '#0369a1', label: 'Active — Phase 3' },
  COMPLETED: { bg: '#dcfce7', color: '#166534', label: 'Completed' },
  CANCELLED: { bg: '#fee2e2', color: '#dc2626', label: 'Cancelled' },
};

const PRIORITY_COLORS: Record<string, { bg: string; color: string; label: string }> = {
  EMERGENCY: { bg: '#fee2e2', color: '#dc2626', label: 'Emergency' },
  HIGH: { bg: '#fef3c7', color: '#b45309', label: 'High Priority' },
  NORMAL: { bg: '#f1f5f9', color: '#475569', label: 'Normal' },
};

function fmt(d?: string) {
  if (!d) return '—';
  try { return new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }); } catch { return d; }
}

function fmtMoney(cents?: number | null) {
  if (!cents) return '—';
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0 }).format(cents / 100);
}

function parseBidNote(note?: string | null): Record<string, string> {
  if (!note) return {};
  try { return JSON.parse(note); } catch { return { details: note }; }
}

// ---------------------------------------------------------------------------
// Acknowledge Modal
// ---------------------------------------------------------------------------
function AcknowledgeModal({ project, token, onAcknowledged, onClose }: {
  project: Project; token: string | null; onAcknowledged: () => void; onClose: () => void;
}) {
  const [typedName, setTypedName] = useState('');
  const [checked, setChecked] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!typedName.trim() || !checked) return;
    setLoading(true); setError('');
    try {
      const r = await fetch(getApiUrl(`/api/property-manager/projects/${project.id}/acknowledge`), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        credentials: 'include',
        body: JSON.stringify({ typedName }),
      });
      if (!r.ok) { const d = await r.json().catch(() => ({})); setError(d.error || 'Failed to acknowledge'); return; }
      onAcknowledged();
    } catch { setError('Network error'); } finally { setLoading(false); }
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
      <div style={{ background: '#fff', borderRadius: 20, padding: 32, width: '100%', maxWidth: 540, boxShadow: '0 20px 60px rgba(0,0,0,0.2)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
          <h2 style={{ fontSize: 20, fontWeight: 800, color: '#0f172a', margin: 0 }}>Sign & Activate Project</h2>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 24, cursor: 'pointer', color: '#94a3b8' }}>×</button>
        </div>
        <div style={{ background: '#f8fafc', borderRadius: 10, padding: '14px 16px', marginBottom: 20, fontSize: 13, color: '#374151', lineHeight: 1.7 }}>
          <strong>Project:</strong> {project.projectType} — {project.property?.label || project.property?.address1 || project.address}<br />
          <strong>Priority:</strong> {project.priority} &nbsp;|&nbsp; <strong>Response By:</strong> {fmt(project.requestedResponseBy)}
        </div>
        <div style={{ background: '#fffbeb', border: '1px solid #fcd34d', borderRadius: 10, padding: '14px 16px', marginBottom: 20, fontSize: 13, color: '#78350f', lineHeight: 1.7 }}>
          By signing below, I confirm that I have the authority to submit this project, that all provided information is accurate, and I authorize CoreBids to share project details with qualified contractors to collect competitive bids. I understand CoreBids is a technology platform facilitating this process, not the contractor.
        </div>
        {error && <div style={{ background: '#fee2e2', color: '#dc2626', padding: '10px 14px', borderRadius: 8, marginBottom: 14, fontSize: 13 }}>{error}</div>}
        <form onSubmit={handleSubmit}>
          <div style={{ marginBottom: 16 }}>
            <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 6 }}>Type your full legal name to sign</label>
            <input type="text" required placeholder="Full legal name" value={typedName} onChange={e => setTypedName(e.target.value)} style={{ width: '100%', padding: '10px 14px', borderRadius: 8, border: '1px solid #d1d5db', fontSize: 14, boxSizing: 'border-box' }} />
          </div>
          <label style={{ display: 'flex', alignItems: 'flex-start', gap: 10, marginBottom: 20, cursor: 'pointer' }}>
            <input type="checkbox" checked={checked} onChange={e => setChecked(e.target.checked)} style={{ marginTop: 2, flexShrink: 0 }} />
            <span style={{ fontSize: 13, color: '#374151', lineHeight: 1.6 }}>I understand and agree to the above acknowledgment. This will activate the project and notify contractors in your area.</span>
          </label>
          <button type="submit" disabled={!typedName.trim() || !checked || loading} style={{ width: '100%', padding: 12, background: BRAND_GREEN, color: '#fff', border: 'none', borderRadius: 10, fontSize: 15, fontWeight: 700, cursor: !typedName.trim() || !checked || loading ? 'not-allowed' : 'pointer', opacity: !typedName.trim() || !checked || loading ? 0.6 : 1 }}>
            {loading ? 'Activating…' : '✓ Sign & Activate Project'}
          </button>
        </form>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Bid Card
// ---------------------------------------------------------------------------
function BidCard({ bid, isSelected, onSelect, selecting }: {
  bid: Bid; isSelected: boolean; onSelect: () => void; selecting: boolean;
}) {
  const noteData = parseBidNote(bid.note);
  return (
    <div style={{ background: isSelected ? '#f0fdf4' : '#fff', border: `2px solid ${isSelected ? '#16a34a' : '#e2e8f0'}`, borderRadius: 14, padding: '20px 22px', transition: 'border-color 0.15s' }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12, marginBottom: 14 }}>
        <div>
          <div style={{ fontSize: 15, fontWeight: 700, color: '#0f172a' }}>{bid.contractor?.company || 'Unknown Contractor'}</div>
          <div style={{ fontSize: 12, color: '#64748b', marginTop: 2 }}>Submitted {fmt(bid.createdAt)}</div>
        </div>
        <div style={{ textAlign: 'right' }}>
          <div style={{ fontSize: 22, fontWeight: 800, color: BRAND_GREEN }}>{fmtMoney(bid.amount ?? undefined)}</div>
          {isSelected && <div style={{ fontSize: 11, fontWeight: 700, color: '#16a34a', marginTop: 2 }}>✓ Selected</div>}
        </div>
      </div>

      {Object.keys(noteData).length > 0 && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: 8, marginBottom: 14 }}>
          {Object.entries(noteData).slice(0, 6).map(([k, v]) => (
            <div key={k} style={{ background: '#f8fafc', borderRadius: 8, padding: '8px 10px' }}>
              <div style={{ fontSize: 10, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 3 }}>{k.replace(/_/g, ' ')}</div>
              <div style={{ fontSize: 12, color: '#374151', fontWeight: 500 }}>{String(v)}</div>
            </div>
          ))}
        </div>
      )}

      {!isSelected && (
        <button
          onClick={onSelect}
          disabled={selecting}
          style={{ width: '100%', padding: '10px', background: BRAND_GREEN, color: '#fff', border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 700, cursor: selecting ? 'not-allowed' : 'pointer', opacity: selecting ? 0.6 : 1 }}
        >
          {selecting ? 'Selecting…' : 'Select This Contractor'}
        </button>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main Page
// ---------------------------------------------------------------------------
interface Props { onNavigate: (screen: string) => void; }

export default function PropertyManagerProjectDetailPage({ onNavigate }: Props) {
  const { getToken } = useAuth();
  const [token, setToken] = useState<string | null>(null);
  const [project, setProject] = useState<Project | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showAcknowledge, setShowAcknowledge] = useState(false);
  const [selecting, setSelecting] = useState<string | null>(null);
  const [selectError, setSelectError] = useState('');
  // CB:BEGIN pm-project-agreement-modal-state
  const [pmAgreementTarget, setPmAgreementTarget] = useState<{ bidId: string; projectAddress?: string } | null>(null);
  // CB:END pm-project-agreement-modal-state

  const projectId = (() => {
    try {
      const parts = window.location.pathname.split('/');
      const idx = parts.indexOf('projects');
      return idx >= 0 ? parts[idx + 1] : null;
    } catch { return null; }
  })();

  const fetchProject = useCallback(async () => {
    if (!projectId) { setError('Project not found'); setLoading(false); return; }
    setLoading(true);
    try {
      const freshToken = await getToken().catch(() => null);
      if (!freshToken) { setLoading(false); return; }
      setToken(freshToken);
      const r = await fetch(getApiUrl(`/api/property-manager/projects/${projectId}`), {
        headers: { Authorization: `Bearer ${freshToken}` }, credentials: 'include',
      });
      if (!r.ok) { setError(r.status === 404 ? 'Project not found' : 'Failed to load project'); return; }
      const d = await r.json();
      setProject(d.project);
    } catch { setError('Failed to load project'); } finally { setLoading(false); }
  }, [getToken, projectId]);

  useEffect(() => { fetchProject(); }, [fetchProject]);

  // CB:BEGIN pm-project-agreement-award-flow
  // handleSelectBid: if pmProjectAckRequired is true and not yet signed, show agreement modal first.
  const handleSelectBid = async (bidId: string) => {
    if (!project || !token) return;
    setSelectError('');

    // Check if project requires PM project-specific agreement and hasn't been signed yet
    const projectAny = project as any;
    if (projectAny.pmProjectAckRequired && !projectAny.pmProjectAckSigned) {
      const prop = project.property;
      const addr = prop
        ? [prop.address1, prop.city, prop.state].filter(Boolean).join(', ')
        : [project.address, project.city, project.state].filter(Boolean).join(', ');
      setPmAgreementTarget({ bidId, projectAddress: addr || undefined });
      return;
    }

    await doAcceptBid(bidId);
  };

  const doAcceptBid = async (bidId: string) => {
    if (!project || !token) return;
    setSelecting(bidId); setSelectError('');
    try {
      const r = await fetch(getApiUrl(`/api/projects/${project.id}/accept-bid`), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        credentials: 'include',
        body: JSON.stringify({ bidId }),
      });
      if (!r.ok) {
        const d = await r.json().catch(() => ({}));
        setSelectError(d.error || d.message || 'Failed to select contractor');
        return;
      }
      await fetchProject(); // refresh to show new status
    } catch { setSelectError('Network error — please try again'); } finally { setSelecting(null); }
  };
  // CB:END pm-project-agreement-award-flow

  const sc = project ? (STATUS_COLORS[project.status] || { bg: '#f1f5f9', color: '#475569', label: project.status }) : null;
  const pc = project ? (PRIORITY_COLORS[project.priority] || PRIORITY_COLORS.NORMAL) : null;
  const meta = (project?.metadata || {}) as Record<string, any>;
  const isAcknowledged = !!meta.pmAcknowledgedAt;
  const isDraft = project?.status === 'DRAFT';
  const canSelectBid = ['BIDS_READY', 'DECIDING', 'BIDS_COLLECTING', 'POSTED'].includes(project?.status || '') && !project?.bids?.find(b => b.isAccepted);
  const selectedBid = project?.bids?.find(b => b.isAccepted);

  if (loading) {
    return (
      <div style={{ minHeight: '100vh', background: '#f8fafc' }}>
        <SharedHeaderPropertyManager onNavigate={onNavigate} activeScreen="pm-projects" />
        <div style={{ textAlign: 'center', padding: '80px 0', color: '#94a3b8', fontSize: 14 }}>Loading project…</div>
      </div>
    );
  }

  if (error || !project) {
    return (
      <div style={{ minHeight: '100vh', background: '#f8fafc' }}>
        <SharedHeaderPropertyManager onNavigate={onNavigate} activeScreen="pm-projects" />
        <ContentLimiter><div style={{ padding: '60px 0', textAlign: 'center' }}>
          <div style={{ fontSize: 16, fontWeight: 700, color: '#dc2626', marginBottom: 12 }}>{error || 'Project not found'}</div>
          <button onClick={() => onNavigate('pm-projects')} style={{ padding: '9px 18px', background: BRAND_GREEN, color: '#fff', border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>Back to Projects</button>
        </div></ContentLimiter>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', background: '#f8fafc' }}>
      <SharedHeaderPropertyManager onNavigate={onNavigate} activeScreen="pm-projects" />

      <ContentLimiter>
        <div style={{ padding: '28px 0' }}>

          {/* Breadcrumb */}
          <button onClick={() => onNavigate('pm-projects')} style={{ display: 'flex', alignItems: 'center', gap: 6, background: 'none', border: 'none', color: '#64748b', fontSize: 13, cursor: 'pointer', padding: '0 0 20px', fontWeight: 500 }}>
            <ArrowLeft size={14} /> Back to Projects
          </button>

          {/* Draft / Signature alert */}
          {isDraft && (
            <div style={{ background: '#fffbeb', border: '1px solid #fcd34d', borderRadius: 12, padding: '16px 20px', marginBottom: 20, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <AlertTriangle size={18} color="#d97706" />
                <div>
                  <div style={{ fontSize: 14, fontWeight: 700, color: '#92400e' }}>Signature Required — Project Not Yet Live</div>
                  <div style={{ fontSize: 12, color: '#b45309', marginTop: 2 }}>Contractors cannot see this project until you sign and activate it.</div>
                </div>
              </div>
              <button onClick={() => setShowAcknowledge(true)} style={{ padding: '9px 18px', background: '#d97706', color: '#fff', border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 700, cursor: 'pointer', whiteSpace: 'nowrap' }}>✏ Sign & Activate</button>
            </div>
          )}

          {/* Bid selection error */}
          {selectError && (
            <div style={{ background: '#fee2e2', color: '#dc2626', padding: '12px 16px', borderRadius: 10, marginBottom: 16, fontSize: 13, fontWeight: 600 }}>
              {selectError}
            </div>
          )}

          {/* Project header card */}
          <div style={{ background: '#fff', borderRadius: 16, border: '1px solid #e2e8f0', padding: '24px 28px', marginBottom: 20, boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }}>
            <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap', marginBottom: 20 }}>
              <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8, flexWrap: 'wrap' }}>
                  <span style={{ display: 'inline-block', background: '#e6f5ef', color: BRAND_GREEN, padding: '3px 10px', borderRadius: 20, fontSize: 11, fontWeight: 700 }}>PM Project</span>
                  {sc && <span style={{ display: 'inline-block', background: sc.bg, color: sc.color, padding: '3px 10px', borderRadius: 20, fontSize: 11, fontWeight: 700 }}>{sc.label}</span>}
                  {pc && <span style={{ display: 'inline-block', background: pc.bg, color: pc.color, padding: '3px 10px', borderRadius: 20, fontSize: 11, fontWeight: 700 }}>{pc.label}</span>}
                </div>
                <h1 style={{ fontSize: 22, fontWeight: 800, color: '#0f172a', margin: '0 0 4px' }}>
                  {project.projectType.charAt(0) + project.projectType.slice(1).toLowerCase()} — {project.property?.label || project.property?.address1 || project.address}
                </h1>
                {project.property && (
                  <div style={{ fontSize: 14, color: '#64748b' }}>
                    {project.property.address1}, {project.property.city}, {project.property.state} {project.property.zip}
                  </div>
                )}
              </div>
              {project.property && (
                <button onClick={() => onNavigate(`/pm/properties/${project.property!.id}`)} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 14px', background: '#f1f5f9', color: '#374151', border: '1px solid #e2e8f0', borderRadius: 8, fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
                  <Building2 size={13} /> View Property
                </button>
              )}
            </div>

            {/* Stats row */}
            <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
              {[
                { icon: <Clock size={14} />, label: 'Response By', value: fmt(project.requestedResponseBy) },
                { icon: <Users size={14} />, label: 'Bids Received', value: String(project.bids?.length ?? 0) },
                { icon: <DollarSign size={14} />, label: 'Selected Bid', value: selectedBid ? fmtMoney(selectedBid.amount ?? undefined) : '—' },
                { icon: <FileText size={14} />, label: 'Acknowledged', value: isAcknowledged ? fmt(meta.pmAcknowledgedAt) : 'Not Yet' },
                { icon: <ClipboardList size={14} />, label: 'Created', value: fmt(project.createdAt) },
              ].map(s => (
                <div key={s.label} style={{ background: '#f8fafc', borderRadius: 10, padding: '10px 14px', minWidth: 120, flex: '1 1 auto' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 10, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 4 }}>{s.icon}{s.label}</div>
                  <div style={{ fontSize: 14, fontWeight: 700, color: s.label === 'Acknowledged' && !isAcknowledged ? '#dc2626' : '#0f172a' }}>{s.value}</div>
                </div>
              ))}
            </div>

            {/* Acknowledgment info if signed */}
            {isAcknowledged && (
              <div style={{ marginTop: 14, display: 'flex', alignItems: 'center', gap: 8, padding: '10px 14px', background: '#f0fdf4', borderRadius: 8, fontSize: 12, color: '#166534' }}>
                <CheckCircle2 size={14} color="#16a34a" />
                Signed by <strong>{meta.pmAcknowledgedName}</strong> on {fmt(meta.pmAcknowledgedAt)} (v{meta.pmAcknowledgedVersion || '1'})
              </div>
            )}
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 320px', gap: 20, alignItems: 'start' }}>
            {/* Left column: bids + details */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>

              {/* Bids section */}
              <div style={{ background: '#fff', borderRadius: 16, border: '1px solid #e2e8f0', overflow: 'hidden', boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }}>
                <div style={{ padding: '18px 22px', borderBottom: '1px solid #f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <h2 style={{ fontSize: 15, fontWeight: 700, color: '#0f172a', margin: 0 }}>
                    {selectedBid ? 'Selected Contractor' : `Bids (${project.bids?.length ?? 0})`}
                  </h2>
                  {selectedBid && <span style={{ background: '#dcfce7', color: '#166534', padding: '3px 10px', borderRadius: 20, fontSize: 11, fontWeight: 700 }}>Contractor Awarded</span>}
                </div>

                <div style={{ padding: '16px 20px' }}>
                  {isDraft ? (
                    <div style={{ textAlign: 'center', padding: '32px 0', color: '#94a3b8' }}>
                      <AlertTriangle size={28} style={{ marginBottom: 10, opacity: 0.4 }} />
                      <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 4 }}>Project not yet live</div>
                      <div style={{ fontSize: 12 }}>Sign & Activate above to start receiving contractor bids.</div>
                    </div>
                  ) : !project.bids?.length ? (
                    <div style={{ textAlign: 'center', padding: '32px 0', color: '#94a3b8' }}>
                      <Users size={28} style={{ marginBottom: 10, opacity: 0.4 }} />
                      <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 4 }}>No bids yet</div>
                      <div style={{ fontSize: 12 }}>Contractors in your area have been notified. Bids typically arrive within 24–72 hours.</div>
                    </div>
                  ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                      {/* Show selected bid first */}
                      {selectedBid && <BidCard bid={selectedBid} isSelected={true} onSelect={() => {}} selecting={false} />}
                      {/* Then unselected bids (only show if no selection yet or showing all) */}
                      {(!selectedBid || canSelectBid) && project.bids?.filter(b => !b.isAccepted).map(bid => (
                        <BidCard
                          key={bid.id}
                          bid={bid}
                          isSelected={false}
                          onSelect={() => handleSelectBid(bid.id)}
                          selecting={selecting === bid.id}
                        />
                      ))}
                      {selectedBid && project.bids && project.bids.filter(b => !b.isAccepted).length > 0 && (
                        <div style={{ fontSize: 12, color: '#94a3b8', textAlign: 'center', padding: '8px 0' }}>
                          {project.bids.filter(b => !b.isAccepted).length} other bid{project.bids.filter(b => !b.isAccepted).length !== 1 ? 's' : ''} received — contractor has already been selected.
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>

              {/* CB:BEGIN pm-post-selection-next-steps */}
              {/* After contractor selection, explain what happens next */}
              {['BID_SELECTED', 'HOMEOWNER_CONTRACT_SIGNING', 'ESCROW_PENDING'].includes(project.status) && (
                <div style={{ background: '#f0f9ff', border: '1px solid #bae6fd', borderRadius: 14, padding: '18px 20px', boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }}>
                  <div style={{ fontSize: 14, fontWeight: 700, color: '#0369a1', marginBottom: 10 }}>What Happens Next</div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    {[
                      { done: true, text: 'Contractor selected and notified' },
                      { done: project.status !== 'BID_SELECTED', text: 'Contract documents prepared and signed' },
                      { done: ['ACTIVE_P1', 'ACTIVE_P2', 'ACTIVE_P3', 'IN_PROGRESS', 'COMPLETED'].includes(project.status), text: 'Escrow funded — work can begin' },
                      { done: project.status === 'COMPLETED', text: 'All milestones completed and funds released' },
                    ].map((step, i) => (
                      <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        <div style={{ width: 18, height: 18, borderRadius: '50%', background: step.done ? '#0369a1' : '#e0f2fe', border: `2px solid ${step.done ? '#0369a1' : '#bae6fd'}`, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          {step.done && <div style={{ width: 6, height: 6, borderRadius: '50%', background: '#fff' }} />}
                        </div>
                        <span style={{ fontSize: 13, color: step.done ? '#0f172a' : '#64748b', fontWeight: step.done ? 600 : 400 }}>{step.text}</span>
                      </div>
                    ))}
                  </div>
                  <div style={{ fontSize: 12, color: '#0369a1', marginTop: 12, padding: '10px 12px', background: 'rgba(3,105,161,0.06)', borderRadius: 8 }}>
                    The CoreBids team is coordinating escrow setup with your selected contractor. You will receive an email when each milestone is ready for your approval.
                  </div>
                </div>
              )}
              {/* CB:END pm-post-selection-next-steps */}

              {/* Project details */}
              {project.extraInfo && (
                <div style={{ background: '#fff', borderRadius: 16, border: '1px solid #e2e8f0', padding: '20px 22px', boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }}>
                  <h2 style={{ fontSize: 15, fontWeight: 700, color: '#0f172a', margin: '0 0 12px' }}>Project Description</h2>
                  <div style={{ fontSize: 13, color: '#374151', lineHeight: 1.7 }}>{project.extraInfo}</div>
                </div>
              )}

              {/* Activity log */}
              <div style={{ background: '#fff', borderRadius: 16, border: '1px solid #e2e8f0', padding: '20px 22px', boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }}>
                <h2 style={{ fontSize: 15, fontWeight: 700, color: '#0f172a', margin: '0 0 14px' }}>Activity Log</h2>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
                  {[
                    { label: 'Project created', date: project.createdAt, color: '#94a3b8' },
                    ...(isAcknowledged ? [{ label: `Signed & activated by ${meta.pmAcknowledgedName}`, date: meta.pmAcknowledgedAt, color: '#16a34a' }] : []),
                    ...(project.bids?.length ? [{ label: `${project.bids.length} bid${project.bids.length !== 1 ? 's' : ''} received`, date: project.bids[0]?.createdAt, color: '#2563eb' }] : []),
                    ...(selectedBid ? [{ label: `Contractor selected: ${selectedBid.contractor?.company}`, date: project.statusEnteredAt || project.updatedAt, color: '#16a34a' }] : []),
                  ].map((e, i) => (
                    <div key={i} style={{ display: 'flex', gap: 12, paddingBottom: 14, marginBottom: 14, borderBottom: '1px solid #f1f5f9' }}>
                      <div style={{ width: 8, height: 8, borderRadius: '50%', background: e.color, marginTop: 5, flexShrink: 0 }} />
                      <div>
                        <div style={{ fontSize: 13, fontWeight: 600, color: '#0f172a' }}>{e.label}</div>
                        <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 2 }}>{fmt(e.date)}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Right sidebar */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              {/* Project timeline */}
              <div style={{ background: '#fff', borderRadius: 16, border: '1px solid #e2e8f0', padding: '18px 20px', boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }}>
                <h3 style={{ fontSize: 14, fontWeight: 700, color: '#0f172a', margin: '0 0 14px' }}>Timeline</h3>
                {[
                  { step: 'Project Created', done: true, date: project.createdAt },
                  { step: 'PM Signature', done: isAcknowledged, date: meta.pmAcknowledgedAt },
                  { step: 'Live to Contractors', done: !isDraft, date: !isDraft ? project.updatedAt : undefined },
                  { step: 'Bids Received', done: (project.bids?.length ?? 0) > 0, date: project.bids?.[0]?.createdAt },
                  { step: 'Contractor Selected', done: !!selectedBid, date: undefined },
                  { step: 'In Progress', done: ['IN_PROGRESS', 'ACTIVE_P1', 'ACTIVE_P2', 'ACTIVE_P3'].includes(project.status), date: undefined },
                  { step: 'Completed', done: project.status === 'COMPLETED', date: undefined },
                ].map((s, i) => (
                  <div key={i} style={{ display: 'flex', gap: 10, marginBottom: 10 }}>
                    <div style={{ width: 18, height: 18, borderRadius: '50%', background: s.done ? BRAND_GREEN : '#f1f5f9', border: `2px solid ${s.done ? BRAND_GREEN : '#e2e8f0'}`, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      {s.done && <div style={{ width: 6, height: 6, borderRadius: '50%', background: '#fff' }} />}
                    </div>
                    <div>
                      <div style={{ fontSize: 12, fontWeight: s.done ? 700 : 400, color: s.done ? '#0f172a' : '#94a3b8' }}>{s.step}</div>
                      {s.date && <div style={{ fontSize: 10, color: '#94a3b8', marginTop: 1 }}>{fmt(s.date)}</div>}
                    </div>
                  </div>
                ))}
              </div>

              {/* PM Notes (internal only) */}
              {project.pmNotes && (
                <div style={{ background: '#fff', borderRadius: 16, border: '1px solid #e2e8f0', padding: '18px 20px', boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 10 }}>
                    <h3 style={{ fontSize: 14, fontWeight: 700, color: '#0f172a', margin: 0 }}>PM Notes</h3>
                    <span style={{ fontSize: 10, fontWeight: 700, background: '#fef3c7', color: '#92400e', padding: '2px 7px', borderRadius: 20 }}>INTERNAL ONLY</span>
                  </div>
                  <div style={{ fontSize: 12, color: '#374151', lineHeight: 1.7 }}>{project.pmNotes}</div>
                </div>
              )}

              {/* Deadline info */}
              <div style={{ background: '#fff', borderRadius: 16, border: '1px solid #e2e8f0', padding: '18px 20px', boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }}>
                <h3 style={{ fontSize: 14, fontWeight: 700, color: '#0f172a', margin: '0 0 12px' }}>Deadlines</h3>
                {[
                  { label: 'Bid Response By', value: fmt(project.requestedResponseBy) },
                  { label: 'Site Visit By', value: fmt(project.requestedSiteVisitBy) },
                ].map(d => (
                  <div key={d.label} style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                    <span style={{ fontSize: 12, color: '#64748b' }}>{d.label}</span>
                    <span style={{ fontSize: 12, fontWeight: 600, color: '#0f172a' }}>{d.value}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </ContentLimiter>

      {showAcknowledge && (
        <AcknowledgeModal
          project={project}
          token={token}
          onAcknowledged={() => { setShowAcknowledge(false); fetchProject(); }}
          onClose={() => setShowAcknowledge(false)}
        />
      )}

      {/* CB:BEGIN pm-project-agreement-modal-render */}
      {pmAgreementTarget && (
        <PMProjectAgreementModal
          projectId={project.id}
          projectAddress={pmAgreementTarget.projectAddress}
          onSigned={async () => {
            // After signing, refresh project state then proceed with bid acceptance
            await fetchProject();
            const bidId = pmAgreementTarget.bidId;
            setPmAgreementTarget(null);
            await doAcceptBid(bidId);
          }}
          onCancel={() => setPmAgreementTarget(null)}
        />
      )}
      {/* CB:END pm-project-agreement-modal-render */}
    </div>
  );
}
// CB:END pm-project-detail-page
